/**
 * Entitlements catalogue + plan matrix.
 *
 * Single source of truth for what each tier can do. Seeded into the `features`
 * and `plan_features` tables and read back at runtime, so gating is a lookup —
 * never a hardcoded plan-name branch. Negotiated exceptions live in
 * `entitlement_overrides`, not in this file.
 */
export type FeatureKind = 'boolean' | 'limit' | 'enum';
export type EntitlementValue = boolean | number | string;

export interface FeatureDef {
  key: string;
  label: string;
  kind: FeatureKind;
  /** Surfaced on the public pricing page. */
  marketed: boolean;
  group: 'events' | 'scoring' | 'branding' | 'ads' | 'money' | 'data' | 'support';
  description: string;
}

/** Sentinel for an unbounded limit. */
export const UNLIMITED = -1;

export const FEATURES: FeatureDef[] = [
  { key: 'events.active', label: 'Active events', kind: 'limit', marketed: true, group: 'events', description: 'Concurrent live or upcoming events.' },
  { key: 'events.per_year', label: 'Events per year', kind: 'limit', marketed: true, group: 'events', description: 'Events created in a rolling 12 months.' },
  { key: 'players.per_event', label: 'Players per event', kind: 'limit', marketed: true, group: 'events', description: 'Entrants in a single event.' },
  { key: 'leaderboards.per_event', label: 'Simultaneous leaderboards', kind: 'limit', marketed: true, group: 'events', description: 'Gross, net, team and division boards running at once.' },
  { key: 'formats.set', label: 'Scoring formats', kind: 'enum', marketed: true, group: 'scoring', description: 'core = the parity set. all = every format in the engine.' },
  { key: 'scoring.live_entry', label: 'Live mobile scoring', kind: 'boolean', marketed: true, group: 'scoring', description: 'Hole-by-hole entry by players and markers.' },
  { key: 'scoring.gps', label: 'GPS rangefinder', kind: 'boolean', marketed: true, group: 'scoring', description: 'Course map, distance to pin and hazard carries.' },
  { key: 'scoring.handicap_index', label: 'Playing Ability index', kind: 'boolean', marketed: true, group: 'scoring', description: 'Index maintained automatically from submitted cards.' },
  { key: 'scoring.handicap_body_sync', label: 'National body sync', kind: 'boolean', marketed: true, group: 'scoring', description: 'Two-way sync with a handicapping union API.' },
  { key: 'branding.custom', label: 'Custom branding', kind: 'boolean', marketed: true, group: 'branding', description: 'Own logo, palette and event artwork.' },
  { key: 'branding.custom_domain', label: 'Custom domain', kind: 'boolean', marketed: true, group: 'branding', description: 'A hostname you control instead of a FairwayLive URL.' },
  { key: 'branding.whitelabel', label: 'Full white-label', kind: 'boolean', marketed: true, group: 'branding', description: 'FairwayLive marks removed entirely.' },
  { key: 'bigscreen.mode', label: 'Clubhouse big screen', kind: 'boolean', marketed: true, group: 'branding', description: 'Fullscreen auto-rotating display mode.' },
  { key: 'ads.suppress_static', label: 'Remove static ads', kind: 'boolean', marketed: true, group: 'ads', description: 'Banner and sidebar inventory hidden from your audience.' },
  { key: 'ads.suppress_interstitial', label: 'Remove pop-ups', kind: 'boolean', marketed: true, group: 'ads', description: 'No interstitial takeovers for your audience.' },
  { key: 'ads.self_sell', label: 'Sell your own inventory', kind: 'boolean', marketed: true, group: 'ads', description: 'Load your own sponsors into your event ad zones.' },
  { key: 'ads.revenue_share_percent', label: 'Ad revenue share', kind: 'limit', marketed: true, group: 'money', description: 'Your cut of FairwayLive-sold inventory on your events.' },
  { key: 'entry_fees.enabled', label: 'Paid online entries', kind: 'boolean', marketed: true, group: 'money', description: 'Collect entry fees through the platform.' },
  { key: 'entry_fees.platform_percent', label: 'Platform fee on entries', kind: 'limit', marketed: true, group: 'money', description: 'Percentage FairwayLive retains per entrant.' },
  { key: 'analytics.basic', label: 'Basic analytics', kind: 'boolean', marketed: true, group: 'data', description: 'Views, impressions, clicks and CTR.' },
  { key: 'analytics.advanced', label: 'Advanced analytics', kind: 'boolean', marketed: true, group: 'data', description: 'Audience breakdowns, benchmarking and engagement cohorts.' },
  { key: 'api.access', label: 'REST API + webhooks', kind: 'boolean', marketed: true, group: 'data', description: 'Programmatic score feeds in and out.' },
  { key: 'exports.csv', label: 'CSV and PDF exports', kind: 'boolean', marketed: true, group: 'data', description: 'Result sheets, prize lists and order of merit.' },
  { key: 'support.tier', label: 'Support', kind: 'enum', marketed: true, group: 'support', description: 'community, email, priority or dedicated.' },
  { key: 'seats.staff', label: 'Staff seats', kind: 'limit', marketed: true, group: 'support', description: 'Admin and scorer logins.' },
];

export interface PlanDef {
  key: string;
  name: string;
  tagline: string;
  /** ZAR cents. 0 = free, null = contact sales. */
  priceMonthlyCents: number | null;
  priceYearlyCents: number | null;
  audience: 'organizer' | 'advertiser';
  highlight?: boolean;
  badge?: string;
  entitlements: Record<string, EntitlementValue>;
}

export const PLANS: PlanDef[] = [
  {
    key: 'free',
    name: 'Fairway Free',
    tagline: 'Run one event and keep the whole scoreboard. We keep the ad breaks.',
    priceMonthlyCents: 0,
    priceYearlyCents: 0,
    audience: 'organizer',
    entitlements: {
      'events.active': 1,
      'events.per_year': 4,
      'players.per_event': 40,
      'leaderboards.per_event': 1,
      'formats.set': 'core',
      'scoring.live_entry': true,
      'scoring.gps': false,
      'scoring.handicap_index': true,
      'scoring.handicap_body_sync': false,
      'branding.custom': false,
      'branding.custom_domain': false,
      'branding.whitelabel': false,
      'bigscreen.mode': true,
      'ads.suppress_static': false,
      'ads.suppress_interstitial': false,
      'ads.self_sell': false,
      'ads.revenue_share_percent': 0,
      'entry_fees.enabled': true,
      'entry_fees.platform_percent': 7.5,
      'analytics.basic': true,
      'analytics.advanced': false,
      'api.access': false,
      'exports.csv': false,
      'support.tier': 'community',
      'seats.staff': 2,
    },
  },
  {
    key: 'club',
    name: 'Club',
    tagline: 'The weekly medal, the club champs and the whole society calendar.',
    priceMonthlyCents: 89_900,
    priceYearlyCents: 899_000,
    audience: 'organizer',
    entitlements: {
      'events.active': 10,
      'events.per_year': 120,
      'players.per_event': 200,
      'leaderboards.per_event': 4,
      'formats.set': 'all',
      'scoring.live_entry': true,
      'scoring.gps': true,
      'scoring.handicap_index': true,
      'scoring.handicap_body_sync': false,
      'branding.custom': true,
      'branding.custom_domain': false,
      'branding.whitelabel': false,
      'bigscreen.mode': true,
      'ads.suppress_static': false,
      'ads.suppress_interstitial': true,
      'ads.self_sell': true,
      'ads.revenue_share_percent': 10,
      'entry_fees.enabled': true,
      'entry_fees.platform_percent': 5,
      'analytics.basic': true,
      'analytics.advanced': false,
      'api.access': false,
      'exports.csv': true,
      'support.tier': 'email',
      'seats.staff': 8,
    },
  },
  {
    key: 'tour',
    name: 'Tour',
    tagline: 'Multi-venue orders of merit, leagues and sponsored series.',
    priceMonthlyCents: 249_900,
    priceYearlyCents: 2_499_000,
    audience: 'organizer',
    highlight: true,
    badge: 'Most popular',
    entitlements: {
      'events.active': UNLIMITED,
      'events.per_year': UNLIMITED,
      'players.per_event': 1_000,
      'leaderboards.per_event': 12,
      'formats.set': 'all',
      'scoring.live_entry': true,
      'scoring.gps': true,
      'scoring.handicap_index': true,
      'scoring.handicap_body_sync': true,
      'branding.custom': true,
      'branding.custom_domain': true,
      'branding.whitelabel': false,
      'bigscreen.mode': true,
      'ads.suppress_static': true,
      'ads.suppress_interstitial': true,
      'ads.self_sell': true,
      'ads.revenue_share_percent': 20,
      'entry_fees.enabled': true,
      'entry_fees.platform_percent': 3.5,
      'analytics.basic': true,
      'analytics.advanced': true,
      'api.access': true,
      'exports.csv': true,
      'support.tier': 'priority',
      'seats.staff': 25,
    },
  },
  {
    key: 'enterprise',
    name: 'Enterprise',
    tagline: 'White-label the whole platform. Your brand, our engine.',
    priceMonthlyCents: null,
    priceYearlyCents: null,
    audience: 'organizer',
    badge: 'White-label',
    entitlements: {
      'events.active': UNLIMITED,
      'events.per_year': UNLIMITED,
      'players.per_event': UNLIMITED,
      'leaderboards.per_event': UNLIMITED,
      'formats.set': 'all',
      'scoring.live_entry': true,
      'scoring.gps': true,
      'scoring.handicap_index': true,
      'scoring.handicap_body_sync': true,
      'branding.custom': true,
      'branding.custom_domain': true,
      'branding.whitelabel': true,
      'bigscreen.mode': true,
      'ads.suppress_static': true,
      'ads.suppress_interstitial': true,
      'ads.self_sell': true,
      'ads.revenue_share_percent': 35,
      'entry_fees.enabled': true,
      'entry_fees.platform_percent': 2,
      'analytics.basic': true,
      'analytics.advanced': true,
      'api.access': true,
      'exports.csv': true,
      'support.tier': 'dedicated',
      'seats.staff': UNLIMITED,
    },
  },
];

/** Paid add-ons stack on top of any organizer plan. */
export interface AddOnDef {
  key: string;
  name: string;
  priceMonthlyCents: number;
  grants: Record<string, EntitlementValue>;
  description: string;
}

export const ADD_ONS: AddOnDef[] = [
  {
    key: 'analytics_pro',
    name: 'Analytics Pro',
    priceMonthlyCents: 79_900,
    grants: { 'analytics.advanced': true },
    description: 'Audience demographics, ad-performance benchmarking and engagement trends.',
  },
  {
    key: 'handicap_sync',
    name: 'Handicap Body Sync',
    priceMonthlyCents: 49_900,
    grants: { 'scoring.handicap_body_sync': true },
    description: 'Push and pull player indexes against your national union feed.',
  },
  {
    key: 'ad_free_audience',
    name: 'Ad-Free Audience',
    priceMonthlyCents: 149_900,
    grants: { 'ads.suppress_static': true, 'ads.suppress_interstitial': true },
    description: 'Buy your audience out of the ad rotation for one billing period.',
  },
  {
    key: 'extra_players_500',
    name: 'Field top-up: +500 players',
    priceMonthlyCents: 39_900,
    grants: { 'players.per_event': 500 },
    description: 'Field-size top-up for a big open or charity day.',
  },
];

export const plan = (key: string): PlanDef | undefined => PLANS.find((p) => p.key === key);
export const feature = (key: string): FeatureDef | undefined => FEATURES.find((f) => f.key === key);
export const addOn = (key: string): AddOnDef | undefined => ADD_ONS.find((a) => a.key === key);

/**
 * Resolve one entitlement from a plan plus any add-ons and overrides.
 * Limits take the most generous value; booleans are OR-ed; enums take the last
 * non-empty grant. Overrides always win outright.
 */
export function resolveEntitlement(
  key: string,
  planKey: string,
  addOnKeys: string[] = [],
  overrides: Record<string, EntitlementValue> = {},
): EntitlementValue | undefined {
  if (key in overrides) return overrides[key];
  const base = plan(planKey)?.entitlements[key];
  let value = base;
  for (const k of addOnKeys) {
    const granted = addOn(k)?.grants[key];
    if (granted === undefined) continue;
    if (typeof granted === 'boolean') value = Boolean(value) || granted;
    else if (typeof granted === 'number' && typeof value === 'number')
      value = value === UNLIMITED || granted === UNLIMITED ? UNLIMITED : Math.max(value, granted);
    else value = granted;
  }
  return value;
}
